import React, { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MinistriesService, type CreateMinisterioDto, type Ministerio } from '../../services/ministries/ministries.service';
import { NotificationToast } from '../ui/NotificationToast';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';
import { Search, X, User } from 'lucide-react';

const createMinisterioSchema = z.object({
  nombre: z.string().min(2, 'Mínimo 2 caracteres'),
  descripcion: z.string().optional(),
  mision: z.string().optional(),
  vision: z.string().optional(),
  liderId: z.string().optional(),
});

type CreateMinisterioFormData = z.infer<typeof createMinisterioSchema>;

interface CreateMinisterioDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  ministerio?: Ministerio | null;
}

export const CreateMinisterioDrawer: React.FC<CreateMinisterioDrawerProps> = ({
  isOpen,
  onClose,
  onSuccess,
  ministerio,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<CreateMinisterioFormData>({
    resolver: zodResolver(createMinisterioSchema),
    defaultValues: {
      nombre: '',
      descripcion: '',
      mision: '',
      vision: '',
      liderId: '',
    },
  });

  const liderId = watch('liderId');
  const isEditing = !!ministerio;

  // Buscador de líder
  const [liderSearch, setLiderSearch] = useState('');
  const [liderResults, setLiderResults] = useState<Array<{ id: string; nombres: string; apellidos: string; email: string }>>([]);
  const [liderLoading, setLiderLoading] = useState(false);
  const [liderSelectedName, setLiderSelectedName] = useState('');
  const [showLiderResults, setShowLiderResults] = useState(false);
  const liderSearchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      if (ministerio) {
        reset({
          nombre: ministerio.nombre,
          descripcion: ministerio.descripcion || '',
          mision: ministerio.mision || '',
          vision: ministerio.vision || '',
          liderId: ministerio.lider?.id || '',
        });
        setLiderSelectedName(ministerio.lider ? `${ministerio.lider.nombres} ${ministerio.lider.apellidos}` : '');
      } else {
        reset({
          nombre: '',
          descripcion: '',
          mision: '',
          vision: '',
          liderId: '',
        });
        setLiderSelectedName('');
      }
      setLiderSearch('');
      setLiderResults([]);
      setShowLiderResults(false);
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      setTimeout(() => {
        setIsLoading(false);
        setErrorMessage(null);
        reset();
        setLiderSelectedName('');
        setLiderSearch('');
        setLiderResults([]);
      }, 300);
    }
  }, [isOpen, reset, ministerio]);

  // Click outside para cerrar resultados de búsqueda de líder
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (liderSearchRef.current && !liderSearchRef.current.contains(event.target as Node)) {
        setShowLiderResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounce búsqueda de líder
  useEffect(() => {
    if (!liderSearch || liderSearch.length < 2) {
      setLiderResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setLiderLoading(true);
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(
          `${API_BASE_URL}${API_ENDPOINTS.memberships.list}?search=${encodeURIComponent(liderSearch)}&limit=10`,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
        if (response.ok) {
          const result = await response.json();
          const data = result.data?.data || result.data || [];
          setLiderResults(
            data.map((m: any) => ({
              id: m.id,
              nombres: m.nombres,
              apellidos: m.apellidos,
              email: m.email,
            }))
          );
          setShowLiderResults(true);
        }
      } catch (err) {
        console.error('Error buscando líder:', err);
      } finally {
        setLiderLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [liderSearch]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const onSubmit = async (data: CreateMinisterioFormData) => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const payload: CreateMinisterioDto = {
        nombre: data.nombre,
        descripcion: data.descripcion || undefined,
        mision: data.mision || undefined,
        vision: data.vision || undefined,
        liderId: data.liderId || null,
      };
      if (isEditing && ministerio) {
        const result = await MinistriesService.update(ministerio.id, payload);
        setNotificationConfig({
          title: '¡Ministerio actualizado!',
          message: `${result.nombre} fue actualizado exitosamente.`,
          type: 'success',
        });
      } else {
        const result = await MinistriesService.create(payload);
        setNotificationConfig({
          title: '¡Ministerio creado!',
          message: `${result.nombre} fue creado exitosamente.`,
          type: 'success',
        });
      }
      setShowNotification(true);
      if (onSuccess) onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error al guardar ministerio:', error);
      const errorMsg = error instanceof Error ? error.message : 'Error desconocido al guardar ministerio';
      setErrorMessage(errorMsg);
      setNotificationConfig({
        title: isEditing ? 'Error al actualizar ministerio' : 'Error al crear ministerio',
        message: errorMsg,
        type: 'error',
      });
      setShowNotification(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => setShowNotification(false)}
        />
      )}
      <div className="fixed left-0 right-0 bottom-0 top-0 z-[70] overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute inset-0 cursor-pointer"
            onClick={handleClose}
            aria-label="Cerrar drawer"
          />
          <section
            className={`absolute right-0 top-0 h-full w-full max-w-2xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out ${
              isVisible ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div
              className="h-full flex flex-col bg-white shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 bg-gray-100 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xs font-medium text-gray-900">{isEditing ? 'Editar Ministerio' : 'Crear Ministerio'}</h2>
                  <button
                    onClick={handleClose}
                    className="text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {errorMessage && (
                  <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex">
                      <svg className="w-5 h-5 text-red-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-red-800 text-xs">{errorMessage}</p>
                    </div>
                  </div>
                )}

                <form id="create-ministerio-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">Nombre *</label>
                    <input
                      type="text"
                      {...register('nombre')}
                      placeholder="Nombre del ministerio"
                      className={`w-full px-4 py-3 border rounded-lg transition-all ${
                        errors.nombre
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                          : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                      } focus:ring-4 focus:outline-none`}
                    />
                    {errors.nombre && <p className="mt-1 text-xs text-red-500">{errors.nombre.message}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">Descripción</label>
                    <textarea
                      {...register('descripcion')}
                      rows={3}
                      placeholder="Descripción del ministerio"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg transition-all focus:border-purple-500 focus:ring-purple-500/20 focus:ring-4 focus:outline-none"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Misión</label>
                      <textarea
                        {...register('mision')}
                        rows={3}
                        placeholder="Misión"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg transition-all focus:border-purple-500 focus:ring-purple-500/20 focus:ring-4 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Visión</label>
                      <textarea
                        {...register('vision')}
                        rows={3}
                        placeholder="Visión"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg transition-all focus:border-purple-500 focus:ring-purple-500/20 focus:ring-4 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">Líder del ministerio</label>
                    <Controller
                      name="liderId"
                      control={control}
                      render={() => (
                        <div className="relative" ref={liderSearchRef}>
                          {liderSelectedName ? (
                            <div className="flex items-center justify-between w-full px-4 py-3 border border-purple-200 bg-purple-50 rounded-lg">
                              <div className="flex items-center">
                                <User className="w-5 h-5 text-purple-600 mr-2" />
                                <span className="text-gray-900 font-medium">{liderSelectedName}</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  setValue('liderId', '');
                                  setLiderSelectedName('');
                                  setLiderSearch('');
                                }}
                                className="text-gray-400 hover:text-red-500 transition-colors"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <>
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                  type="text"
                                  value={liderSearch}
                                  onChange={(e) => setLiderSearch(e.target.value)}
                                  onFocus={() => {
                                    if (liderResults.length > 0) setShowLiderResults(true);
                                  }}
                                  placeholder="Buscar miembro por nombre..."
                                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg transition-all focus:border-purple-500 focus:ring-purple-500/20 focus:ring-4 focus:outline-none"
                                />
                                {liderLoading && (
                                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                                  </div>
                                )}
                              </div>
                              {showLiderResults && liderResults.length > 0 && (
                                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                                  {liderResults.map((miembro) => (
                                    <button
                                      key={miembro.id}
                                      type="button"
                                      onClick={() => {
                                        setValue('liderId', miembro.id);
                                        setLiderSelectedName(`${miembro.nombres} ${miembro.apellidos}`);
                                        setLiderSearch('');
                                        setShowLiderResults(false);
                                        setLiderResults([]);
                                      }}
                                      className="w-full px-4 py-3 text-left hover:bg-purple-50 border-b border-gray-100 last:border-b-0 transition-colors"
                                    >
                                      <div className="font-medium text-gray-900">
                                        {miembro.nombres} {miembro.apellidos}
                                      </div>
                                      <div className="text-xs text-gray-500">{miembro.email}</div>
                                    </button>
                                  ))}
                                </div>
                              )}
                              {showLiderResults && liderSearch.length >= 2 && !liderLoading && liderResults.length === 0 && (
                                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-xs text-gray-500">
                                  No se encontraron miembros
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    />
                  </div>
                </form>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    form="create-ministerio-form"
                    disabled={isLoading}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isLoading && (
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    Guardar
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};
